(function () {
  const MIN_BUNDLE_SIZE = window.bundleConfig?.minBundleSize || 4;
  // Captured before the wrapper below replaces window.fetch, so the watcher's own cart calls are
  // invisible to it. Previously a flag muted the wrapper for the whole run, which meant a quantity
  // the customer changed mid-run was never noticed.
  const originalFetch = window.fetch.bind(window);
  let isRunning = false;
  let pendingCheck = false;
  let debounceTimer = null;

  function getBundleByIndex(cart) {
    const bundleByIndex = {};
    cart.items.forEach((item) => {
      if (item.properties?._is_bundle !== "true") return;
      const idx = Number(item.properties?._dynamicBoxIndex);
      if (isNaN(idx)) return;
      if (!bundleByIndex[idx]) {
        bundleByIndex[idx] = {
          totalQty: 0,
          isSubscription: false,
          giftMultiplier: 1,
          bundleSize: 0,
          giftStep: 0,
          giftIds: null,
        };
      }
      bundleByIndex[idx].totalQty += item.quantity;
      if (item.selling_plan_allocation) {
        bundleByIndex[idx].isSubscription = true;
      }
      // A double subscription is several boxes' worth, so it earns that many of each gift. The
      // builder stamps the count on every bundle line; without it a box gets the usual single gift.
      const multiplier = Number(item.properties?._gift_multiplier);
      if (Number.isFinite(multiplier) && multiplier > bundleByIndex[idx].giftMultiplier) {
        bundleByIndex[idx].giftMultiplier = multiplier;
      }
      // The size the box was added at, e.g. "8_bags" for a double of a 4-box.
      const bundleSize = parseInt(item.properties?._bundle_size);
      if (Number.isFinite(bundleSize) && bundleSize > bundleByIndex[idx].bundleSize) {
        bundleByIndex[idx].bundleSize = bundleSize;
      }
      // Items in one gift-earning box. A sectioned box is size x sections, so this can't be
      // derived from _bundle_size, which only records the per-section size.
      const giftStep = parseInt(item.properties?._gift_step);
      if (Number.isFinite(giftStep) && giftStep > bundleByIndex[idx].giftStep) {
        bundleByIndex[idx].giftStep = giftStep;
      }
      // The gifts this box was built to offer. A builder with its special offer blocks turned off
      // stamps "none", so no gift is invented for it; null means the box predates the property.
      const giftIds = item.properties?._gift_ids;
      if (typeof giftIds === "string" && giftIds) {
        bundleByIndex[idx].giftIds = giftIds === "none" ? [] : giftIds.split(",").filter(Boolean);
      }
    });
    return bundleByIndex;
  }

  // Boxes added before _gift_ids existed fall back to the theme-wide list so their gifts keep
  // working; every box added since carries its own list, empty ones included.
  function getBundleGiftIds(bundle) {
    if (bundle?.giftIds) return bundle.giftIds;
    return (window.bundleConfig?.giftVariantIds || []).map(String);
  }

  // How many of each gift a box currently earns. Gifts follow what is still in the cart rather than
  // what was added, so emptying a double box back down to a single one drops the extra gift. Any
  // box over the minimum earns the single gift; the extra ones need a full box each on top.
  // A double of a 2-section 4-box (16 items, 8 per box): 2 gifts at 16, 1 from 4, none below 4.
  function getGiftAllowance(bundle) {
    if (!bundle || !bundle.isSubscription) return 0;
    if (bundle.totalQty < MIN_BUNDLE_SIZE) return 0;

    const multiplier = bundle.giftMultiplier || 1;
    const stepSize = bundle.giftStep || (bundle.bundleSize ? bundle.bundleSize / multiplier : 0);

    // Carts predating these properties get the single gift they would have had before.
    if (!stepSize) return 1;

    const earned = Math.floor(bundle.totalQty / stepSize);
    return Math.min(Math.max(earned, 1), multiplier);
  }

  function lockGiftItems(cart) {
    const giftVariantIds = cart.items.filter((i) => i.properties?._is_gift === "true").map((i) => String(i.variant_id));

    if (!giftVariantIds.length) return;

    document.querySelectorAll("tr.cart-item").forEach((row) => {
      const input = row.querySelector("input.quantity__input");
      if (!input) return;
      const variantId = String(input.dataset.quantityVariantId || "");
      if (!giftVariantIds.includes(variantId)) return;

      const priceEl = row.querySelector(".price--end");
      const isFree = priceEl?.textContent?.trim().includes("$0.00");
      if (!isFree) return;

      row.classList.add("cart-item--gift");
      input.disabled = true;
      input.style.pointerEvents = "none";
      row.querySelectorAll(".quantity__button").forEach((btn) => {
        btn.style.pointerEvents = "none";
      });
    });
  }

  async function checkCart() {
    // A change made while a pass is in flight is remembered and handled by a follow-up pass rather
    // than dropped, so quickly stepping a quantity down still settles on the right gift count.
    if (isRunning) {
      pendingCheck = true;
      return;
    }
    if (window._bundleGiftAddInProgress) return;
    isRunning = true;

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const res = await originalFetch("/cart.js?" + Date.now());
      const cart = await res.json();

      const bundleByIndex = getBundleByIndex(cart);
      const giftItems = cart.items.filter((i) => i.properties?._is_gift === "true");

      // Any of the three passes below can change the cart, and all of them need the drawer
      // re-rendered afterwards — a trim from two gifts down to one is just as much a change as
      // adding one, and used to be missed because only additions were counted.
      let didMutateCart = false;

      const updates = {};
      giftItems.forEach((item) => {
        const idx = Number(item.properties?._dynamicBoxIndex);
        // Anything the box no longer earns is trimmed by the normalize pass below; this only drops
        // gifts whose box has gone away, stopped being a subscription, or fallen under the minimum.
        if (getGiftAllowance(bundleByIndex[idx]) === 0) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await originalFetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        didMutateCart = true;
      }

      const itemsToAdd = [];
      const topUpUpdates = {};
      Object.entries(bundleByIndex).forEach(([idx, bundle]) => {
        const expectedGiftQty = getGiftAllowance(bundle);
        if (expectedGiftQty === 0) return;

        const giftVariantIds = getBundleGiftIds(bundle);
        if (!giftVariantIds.length) return;
        giftVariantIds.forEach((giftId) => {
          const giftsInCart = giftItems.filter(
            (i) => String(i.variant_id) === String(giftId) && Number(i.properties?._dynamicBoxIndex) === Number(idx),
          );
          const totalGiftQty = giftsInCart.reduce((sum, i) => sum + i.quantity, 0);
          const shortfall = expectedGiftQty - totalGiftQty;
          if (shortfall <= 0) return;

          // Raise the existing row by its line key rather than posting to /cart/add.js, which only
          // merges when every line item property matches and otherwise adds a second row.
          if (giftsInCart.length) {
            topUpUpdates[giftsInCart[0].key] = giftsInCart[0].quantity + shortfall;
            return;
          }

          itemsToAdd.push({
            id: Number(giftId),
            quantity: shortfall,
            // Matches what the builder stamps, so a later top-up lands on this same row.
            properties: {
              _dynamicBoxIndex: Number(idx),
              _is_gift: "true",
              _bundle_size: String(bundle.bundleSize || MIN_BUNDLE_SIZE) + "_bags",
            },
          });
        });
      });

      if (Object.keys(topUpUpdates).length) {
        await originalFetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: topUpUpdates }),
        });
        didMutateCart = true;
      }

      if (itemsToAdd.length) {
        await originalFetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: itemsToAdd }),
        });
        didMutateCart = true;
      }

      const normalizeRes = await originalFetch("/cart.js?" + Date.now());
      const normalizeCart = await normalizeRes.json();
      const normalizeBundles = getBundleByIndex(normalizeCart);
      const normalizeUpdates = {};

      // Trim any gift the customer has bumped above what its box earns. Grouped per box + variant
      // because a top-up can land on its own line, and the allowance covers the group, not a line.
      const giftGroups = new Map();
      normalizeCart.items.forEach((item) => {
        if (item.properties?._is_gift !== "true") return;
        const idx = Number(item.properties?._dynamicBoxIndex);
        const bundle = normalizeBundles[idx];
        // A gift its box does not offer is cleared outright, which also empties carts that picked
        // one up while the watcher went by the theme-wide list.
        const isOffered = getBundleGiftIds(bundle).some((id) => String(id) === String(item.variant_id));
        const key = `${idx}|${item.variant_id}`;
        if (!giftGroups.has(key)) {
          giftGroups.set(key, { expected: isOffered ? getGiftAllowance(bundle) : 0, items: [] });
        }
        giftGroups.get(key).items.push(item);
      });

      // Collapse each group onto its first row and trim it to what the box earns, so a gift never
      // shows as two rows and never exceeds its allowance. This also tidies carts that picked up
      // duplicate rows before top-ups were done by line key.
      giftGroups.forEach((group) => {
        const total = group.items.reduce((sum, item) => sum + item.quantity, 0);
        const keep = Math.min(total, group.expected);

        group.items.forEach((item, i) => {
          const target = i === 0 ? keep : 0;
          if (item.quantity !== target) normalizeUpdates[item.key] = target;
        });
      });

      if (Object.keys(normalizeUpdates).length) {
        await originalFetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: normalizeUpdates }),
        });
        didMutateCart = true;
      }

      if (!didMutateCart) {
        lockGiftItems(cart);
        return;
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.updateContents) {
        await cartDrawer.updateContents();
      } else if (cartDrawer?.updateContentsAndOpen) {
        const originalOpen = cartDrawer.open?.bind(cartDrawer);
        cartDrawer.open = () => {};
        await cartDrawer.updateContentsAndOpen?.();
        if (originalOpen) cartDrawer.open = originalOpen;
      }

      const res2 = await originalFetch("/cart.js?" + Date.now());
      const cart2 = await res2.json();
      lockGiftItems(cart2);
    } catch (err) {
      console.error("Cart gift watcher error:", err);
    } finally {
      isRunning = false;

      if (pendingCheck) {
        pendingCheck = false;
        scheduleCheck();
      }
    }
  }

  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkCart, 800);
  }

  window.fetch = async (...args) => {
    const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
    const result = await originalFetch(...args);

    const isCartMutation = url.includes("/cart/update") || url.includes("/cart/change") || url.includes("/cart/remove");

    if (isCartMutation) scheduleCheck();

    return result;
  };

  document.addEventListener("DOMContentLoaded", () => {
    checkCart();

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      new MutationObserver(() => {
        originalFetch("/cart.js")
          .then((r) => r.json())
          .then(lockGiftItems);
      }).observe(cartDrawer, { childList: true, subtree: true });

      const onOpen = () => {
        setTimeout(
          () =>
            originalFetch("/cart.js")
              .then((r) => r.json())
              .then(lockGiftItems),
          150,
        );
        setTimeout(
          () =>
            originalFetch("/cart.js")
              .then((r) => r.json())
              .then(lockGiftItems),
          500,
        );
      };

      cartDrawer.addEventListener("show", onOpen);
      document.addEventListener("cart:open", onOpen);
      document.addEventListener("on:cart-drawer:open", onOpen);

      new MutationObserver(() => {
        const isOpen =
          cartDrawer.hasAttribute("open") ||
          cartDrawer.classList.contains("active") ||
          cartDrawer.classList.contains("is-open");
        if (isOpen) {
          setTimeout(
            () =>
              originalFetch("/cart.js")
                .then((r) => r.json())
                .then(lockGiftItems),
            150,
          );
        }
      }).observe(cartDrawer, { attributes: true, attributeFilter: ["open", "class"] });
    }
  });
})();
